import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { Page, ProfileKind, SharedContactModel } from '../../../../types';
import DeleteContactsDialog from './delete-contacts-dialog';
import AddSharedContactsDialog from './add-contacts-dialog';
import EditContactDialog from './edit-contacts-dialog';
import SharedContactsGridToolbar from './shared-contacts-toolbar';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../../utils/use-user-settings';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  getSharedContacts,
  UserKeys,
  userPortalGetSharedContacts,
} from '../../../api/users-api';
import { Messages } from '../../../utils/message-constants';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import PersonOff from '@mui/icons-material/PersonOff';

interface SharedContactsGridProps {
  userView?: boolean;
}

export default function SharedContactsGrid({
  userView,
}: SharedContactsGridProps) {
  const { directoryRoles, userViewRoles, primaryDomain, viewType } =
    useUserSettings();
  const [openEditContact, setOpenEditContact] = useState(false);
  const [selectedContact, setSelectedContact] =
    useState<SharedContactModel | null>(null);
  const [openAddContacts, setOpenAddContacts] = useState(false);
  const [openDeleteContacts, setOpenDeleteContacts] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const columns: GridColDef<SharedContactModel>[] = useMemo(
    () => [
      {
        field: 'emails',
        headerName: 'Email',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => {
          if (params.value) {
            const email: ProfileKind[] = params.value.filter(
              (email: ProfileKind) => {
                if (email.rel === 'Primary') {
                  return email;
                }
              }
            );
            return email.length > 0 ? email[0].val : params.value[0].val;
          } else {
            return '';
          }
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
    ],
    []
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'sharedContactsGridStates'
  );

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 50,
    page: 0,
    cursor: null,
    search: initialState?.filter?.filterModel?.quickFilterValues?.[0] || '',
  });

  const {
    data: contacts,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      UserKeys.SharedContacts,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.search,
      userView,
    ],
    queryFn: () => {
      const object: Page<SharedContactModel[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      if (paginationModel.page !== 0 && contacts?.queries) {
        object.queries = {
          [Object.keys(contacts.queries)[0]]: paginationModel.cursor || '',
        };
      }
      const queryFn = userView
        ? userPortalGetSharedContacts
        : getSharedContacts;
      return queryFn(
        primaryDomain,
        paginationModel.search,
        object,
        paginationModel.pageSize
      );
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('shared contacts'),
    },
  });

  const toolbarProps = useMemo(
    () => ({
      userView,
      paginationModel,
      setPaginationModel,
      directoryRoles: directoryRoles,
      userViewRoles: userViewRoles,
      setOpenAddContacts,
      setOpenDeleteContacts,
      selectedRows,
    }),
    [directoryRoles, paginationModel, selectedRows, userView, userViewRoles]
  );

  const CustomToolbar = useToolbar(SharedContactsGridToolbar, toolbarProps);

  const sharedContactsLoading = isLoading || isFetching;

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays(
            'Contacts',
            <PersonOff />,
            'Try changing your search term.'
          ),
        }}
        initialState={
          initialState || {
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: 50,
              },
              rowCount: 0,
            },
          }
        }
        disableColumnMenu
        checkboxSelection
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        data={contacts}
        columns={computedColumns}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (sharedContactsLoading) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              search: paginationModel.search,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: contacts?.cursor || '',
              search: paginationModel.search,
            });
          }
        }}
        onFilterModelChange={(model) => {
          if (sharedContactsLoading) return;

          setPaginationModel({
            ...paginationModel,
            page: 0,
            cursor: null,
            search: model.quickFilterValues?.[0] || '',
          });
        }}
        loading={sharedContactsLoading}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        setSelectedRows={setSelectedRows}
        onRowClick={(row) => {
          const selectedContact = (contacts?.results || []).find(
            (contact) => contact.id === row.row.id
          )!;
          setSelectedContact(selectedContact);
          setOpenEditContact(true);
        }}
        selectedRows={selectedRows}
        gridKey={
          viewType.sharedContactsGridStates?.activeGridState || 'default'
        }
      />
      {selectedRows.length > 0 ? (
        <DeleteContactsDialog
          open={openDeleteContacts}
          handleClose={() => {
            setOpenDeleteContacts(false);
          }}
          userView={userView}
          contactList={contacts?.results || []}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          searchTerm={paginationModel.search}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      ) : null}
      <AddSharedContactsDialog
        open={openAddContacts}
        handleClose={() => {
          setOpenAddContacts(false);
        }}
        userView={userView}
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
      />
      {selectedContact ? (
        <EditContactDialog
          open={openEditContact}
          handleClose={() => setOpenEditContact(false)}
          contactList={contacts?.results || []}
          selectedContact={selectedContact}
          setSelectedContact={setSelectedContact}
          userView={userView}
          searchTerm={paginationModel.search}
          primaryDomain={primaryDomain}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
        />
      ) : null}
    </>
  );
}
